import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Instagram, 
  Twitter, 
  Facebook, 
  Mail, 
  MapPin, 
  Phone 
} from 'lucide-react';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-[#0f172a] text-gray-300 mt-auto border-t border-gray-800">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 items-center">
            <div className="lg:col-span-2">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1.5">Join our newsletter</h3>
              <p className="text-sm text-gray-400">Get the latest updates on exclusive physical art pieces.</p>
            </div>
            <form onSubmit={(e) => e.preventDefault()} className="lg:col-span-2 flex items-stretch">
              <input 
                type="email" 
                aria-label="Email address for newsletter"
                placeholder="Enter your email" 
                className="w-full bg-slate-800/90 border border-slate-600 text-white placeholder-gray-400 px-4 py-2.5 rounded-l-xl focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all text-sm"
              />
              <button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-r-xl font-semibold text-sm transition-all shadow-sm shrink-0"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-8">
          
          {/* Brand Column */}
          <div className="col-span-1 md:col-span-1 lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <img src="/Group 21.png" alt="Artist Network International Logo" className="h-12 w-auto object-contain brightness-0 invert" />
              <p className="text-sm font-medium text-purple-300 tracking-widest uppercase mt-1">Marketplace</p>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              The premier destination for discovering, collecting and trading premium physical artworks from visionary independent artists globally.
            </p>
            <div className="flex space-x-4">
              <a href="#" aria-label="Instagram" className="p-2 bg-gray-800 rounded-full hover:bg-purple-600 hover:text-white transition-colors"><Instagram size={20} /></a>
              <a href="#" aria-label="Twitter" className="p-2 bg-gray-800 rounded-full hover:bg-blue-500 hover:text-white transition-colors"><Twitter size={20} /></a>
              <a href="#" aria-label="Facebook" className="p-2 bg-gray-800 rounded-full hover:bg-blue-600 hover:text-white transition-colors"><Facebook size={20} /></a>
            </div>
          </div>

          {/* Explore Links */}
          <div className="col-span-1 md:col-span-1 lg:col-span-2">
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Explore</h4>
            <ul className="space-y-4">
              <li><Link to="/explore" className="text-gray-400 hover:text-purple-400 transition-colors">All Artworks</Link></li>
              <li><Link to="/explore?category=paintings" className="text-gray-400 hover:text-purple-400 transition-colors">Paintings</Link></li>
              <li><Link to="/explore?category=sculptures" className="text-gray-400 hover:text-purple-400 transition-colors">Sculptures</Link></li>
              <li><Link to="/explore?category=photography" className="text-gray-400 hover:text-purple-400 transition-colors">Photography</Link></li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="col-span-1 md:col-span-1 lg:col-span-2">
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Company</h4>
            <ul className="space-y-4">
              <li><Link to="/about" className="text-gray-400 hover:text-purple-400 transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-purple-400 transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="text-gray-400 hover:text-purple-400 transition-colors">Terms of Service</Link></li>
              <li><Link to="/fees" className="text-gray-400 hover:text-purple-400 transition-colors">Platform Fees</Link></li>
              <li><Link to="/sla" className="text-gray-400 hover:text-purple-400 transition-colors">Seller SLA</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-400 hover:text-purple-400 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/sitemap" className="text-gray-400 hover:text-purple-400 transition-colors">Sitemap</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-1 md:col-span-1 lg:col-span-4 min-w-0">
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin size={18} className="text-purple-400 mr-2.5 mt-0.5 shrink-0" aria-hidden="true" />
                <span className="text-gray-400">Karnataka, India</span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="text-purple-400 mr-2.5 shrink-0" aria-hidden="true" />
                <a href="tel:+919611391739" className="text-gray-400 hover:text-purple-400 transition-colors whitespace-nowrap">+91 9611391739</a>
              </li>
              <li className="flex items-start">
                <Mail size={18} className="text-purple-400 mr-2.5 mt-0.5 shrink-0" aria-hidden="true" />
                <a 
                  href="mailto:Admin@artistnetworkinternational.com" 
                  title="Admin@artistnetworkinternational.com"
                  className="text-gray-400 hover:text-purple-400 transition-colors text-xs sm:text-sm whitespace-nowrap"
                >
                  Admin@artistnetworkinternational.com
                </a>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Copyright */}
      <div className="bg-[#0b1120] py-6 border-t border-gray-800 text-center">
        <p className="text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Artist Network International (ANI). All rights reserved.
        </p>
      </div>
    </footer>
  );
};
